/**
 * What FLY SINGLE MISSION can actually offer today.
 *
 * A catalog entry is not the same thing as a `MissionType`: the type list is
 * the game's ten, a fixed fact about the original, while the catalog is this
 * port's own playable menu. "Dogfight a Famous Ace" is a catalog entry whose
 * *type* is `dogfight`, exactly as it is in `PS.EXE` — a sibling mode that
 * builds a dogfight with a named opponent.
 *
 * Every entry is a pure `(options) => MissionSpec`, so the select screen can
 * generate a briefing card without starting anything, and re-generate it on a
 * new seed as many times as the player likes.
 *
 * **The eight Historic Encounters are entries too, and they are the exception
 * to the sentence above.** `HISTORIC.DAT` names both pilots, both aeroplanes
 * and what happened, so `historic-1` … `historic-8` ignore the seed entirely
 * (`generators-historic.ts`) and read `MissionOptions.nation` as the
 * original's own `BRITISH` / `GERMAN` picker. They are in the same list as
 * everything else so that one URL shape, one launch path and one briefing
 * screen serve every mission this port can fly.
 */

import { generateHistoric } from "./generators-historic.ts";
import { HISTORIC_ENCOUNTERS } from "./historic-encounters.ts";
import { generateScramble } from "./generators-scramble.ts";
import { generateSquadronFight } from "./generators-squadron.ts";
import { generateAceDuel, generateEscortRecon, generatePatrol } from "./generators-sortie.ts";
import { generateBalloonDefense, generateZeppelinIntercept } from "./generators-defence.ts";
import { generateEscortBombers, generateStopBombers } from "./generators-raid.ts";
import { generateBalloonBusting, generateZeppelinHunt } from "./generators-strike.ts";
import type { MissionOptions } from "./mission-options.ts";
import type { MissionSpec } from "./mission-spec.ts";
import { MISSION_BUILDER_LABELS, MISSION_TYPE_LABELS, type MissionType } from "./mission-types.ts";

export interface MissionCatalogEntry {
  readonly id: string;
  readonly type: MissionType;
  /** What the menu calls it. The ace duel uses `PS.EXE`'s sibling-mode wording. */
  readonly label: string;
  /** One line under the label on the select screen. */
  readonly summary: string;
  readonly generate: (options: MissionOptions) => MissionSpec;
}

function entry(
  id: string,
  type: MissionType,
  label: string,
  summary: string,
  generate: (options: MissionOptions) => MissionSpec,
): MissionCatalogEntry {
  return { id, type, label, summary, generate };
}

/**
 * `historic-n` for each of `HISTORIC.DAT`'s eight, typed by what the encounter
 * turns out to be: the three with Drachen on the map are the balloon types, the
 * other five are dogfights. The summary is the encounter's own date and seats.
 */
const HISTORIC_ENTRIES: readonly MissionCatalogEntry[] = HISTORIC_ENCOUNTERS.map((data) =>
  entry(
    data.id,
    (data.balloons ?? []).length > 0 ? "balloonBusting" : "dogfight",
    data.title.toUpperCase(),
    `${data.when}. ${data.german.playable && data.allied.playable ? "Playable from either side." : "German seat only — the original refuses the other."}`,
    (options) => generateHistoric(data.id, options),
  ),
);

export const MISSION_CATALOG: readonly MissionCatalogEntry[] = [
  ...HISTORIC_ENTRIES,
  entry(
    "dogfight-ace",
    "dogfight",
    "DOGFIGHT A FAMOUS ACE",
    "One of the twenty-five named aces, on his own historical mount, coming head-on.",
    generateAceDuel,
  ),
  entry(
    "dogfight-squadron",
    "dogfight",
    "DOGFIGHT A SQUADRON",
    "A named enemy squadron's flight, led by whichever of its aces was on strength — and its own decoded share of veterans.",
    generateSquadronFight,
  ),
  entry(
    "patrol",
    "patrol",
    MISSION_TYPE_LABELS.patrol,
    "Fly the line out to the turn point and back, and deal with whoever is working the same stretch.",
    generatePatrol,
  ),
  entry(
    "escort-recon",
    "escortRecon",
    MISSION_TYPE_LABELS.escortRecon,
    "A two-seater crosses the lines to photograph. Bring it home.",
    generateEscortRecon,
  ),
  entry(
    "balloon-busting",
    "balloonBusting",
    MISSION_TYPE_LABELS.balloonBusting,
    "Two Drachen over the enemy rear, a belt of incendiary, and a standing patrol overhead.",
    generateBalloonBusting,
  ),
  entry(
    "scramble",
    "scramble",
    MISSION_TYPE_LABELS.scramble,
    "Your field is under attack and you are still on the grass. Full throttle, tail up, and get among them.",
    generateScramble,
  ),
  entry(
    "hunt-zeppelin",
    "destroyZeppelin",
    MISSION_TYPE_LABELS.destroyZeppelin,
    "An airship running for Germany, and one escort between you and it.",
    generateZeppelinHunt,
  ),
  entry(
    "escort-bombers",
    "escortBombers",
    MISSION_TYPE_LABELS.escortBombers,
    "Heavies going for a rail yard, a factory and a supply dump at 1,200 feet. See that they succeed.",
    generateEscortBombers,
  ),
  entry(
    "stop-bombers",
    "stopBombers",
    MISSION_TYPE_LABELS.stopBombers,
    "The same raid, inbound, and yours are the yards underneath it.",
    generateStopBombers,
  ),
  entry(
    "intercept-zeppelin",
    "interceptZeppelin",
    MISSION_BUILDER_LABELS.interceptZeppelin.toUpperCase(),
    "An airship on its way in, and a city behind you it must be stopped short of.",
    generateZeppelinIntercept,
  ),
  entry(
    "balloon-defense",
    "balloonDefense",
    MISSION_TYPE_LABELS.balloonDefense,
    "Two of our Drachen up, and a flight coming for them with incendiary.",
    generateBalloonDefense,
  ),
];

export const MISSION_CATALOG_IDS: readonly string[] = MISSION_CATALOG.map((item) => item.id);

export function catalogEntry(id: string): MissionCatalogEntry | undefined {
  return MISSION_CATALOG.find((item) => item.id === id);
}

/** Generates the mission a catalog id names, or throws if the id is not in the menu. */
export function generateMission(id: string, options: MissionOptions): MissionSpec {
  const item = catalogEntry(id);
  if (!item) throw new Error(`no mission catalog entry "${id}"`);
  return item.generate(options);
}
